import { DateRange } from '@/types/dashboard.types';
import type { TrendlineDataPoint } from '@/types/dashboard.types';

// Prefix word for the value sublabel, e.g. "30-day", "7-day", "All-time".
export function getRangeWord(dateRange: DateRange): string {
  switch (dateRange) {
    case DateRange.LAST_7_DAYS:
      return '7-day';
    case DateRange.LAST_30_DAYS:
      return '30-day';
    default:
      return 'All-time';
  }
}

// Label shown below the big number, e.g. "30-day score", "All-time total count".
export function getValueSublabel(
  dateRange: DateRange,
  noun: 'count' | 'score' | 'percentage',
): string {
  const phrase = noun === 'count' ? 'total count' : noun;
  return `${getRangeWord(dateRange)} ${phrase}`;
}

// Label for the change row, e.g. "vs previous 30 days" / "in last 7 days".
export function getChangeLabel(dateRange: DateRange): string {
  switch (dateRange) {
    case DateRange.LAST_7_DAYS:
      return 'vs previous 7 days';
    case DateRange.LAST_30_DAYS:
      return 'vs previous 30 days';
    default:
      return 'in last 7 days';
  }
}

// Color class for a change row: gray for no change, green for increase, red for decrease.
export function getChangeColorClass(
  isIncrease: boolean,
  isZero: boolean,
): string {
  if (isZero) return 'analytics-card-change-neutral';
  return isIncrease
    ? 'analytics-card-change-increase'
    : 'analytics-card-change-decrease';
}

// Aggregation period parsed from the server's trendlinePeriodLabel
// ("daily score trend" / "weekly score trend" / "monthly score trend").
export function getAggregationPeriod(
  periodLabel?: string,
): 'daily' | 'weekly' | 'monthly' {
  if (periodLabel?.includes('weekly')) return 'weekly';
  if (periodLabel?.includes('monthly')) return 'monthly';
  return 'daily';
}

function getPeriodAdjective(
  periodLabel?: string,
): 'Daily' | 'Weekly' | 'Monthly' {
  const period = getAggregationPeriod(periodLabel);
  return period === 'weekly'
    ? 'Weekly'
    : period === 'monthly'
    ? 'Monthly'
    : 'Daily';
}

// Inclusive span in days from the first trendline point through today. This
// matches the window the bar chart actually renders (first data point → today),
// so the "(last N …)" label can't undercount when the newest data is stale.
function trendlineSpanDays(trendline: TrendlineDataPoint[]): number {
  if (trendline.length === 0) return 0;
  const times = trendline.map((d) => new Date(d.date).getTime());
  const spanMs = Date.now() - Math.min(...times);
  return Math.floor(spanMs / (1000 * 60 * 60 * 24)) + 1;
}

// Top-left chart label, e.g. "Daily scores (last 30 days)" or, for the
// all-data range, rounded-up weeks/months: "Weekly scores (last 7 weeks)".
export function getChartLabel(
  dateRange: DateRange,
  periodLabel: string | undefined,
  trendline: TrendlineDataPoint[],
  noun: 'scores' | 'counts' | 'percentages' | 'responses',
): string {
  const adjective = getPeriodAdjective(periodLabel);

  if (dateRange === DateRange.LAST_7_DAYS) {
    return `${adjective} ${noun} (last 7 days)`;
  }
  if (dateRange === DateRange.LAST_30_DAYS) {
    return `${adjective} ${noun} (last 30 days)`;
  }

  // all_data: with no data there's no window to describe — just the type
  if (trendline.length === 0) {
    return `${adjective} ${noun}`;
  }

  // all_data: derive a rounded-up count from the rendered window (first → today)
  const spanDays = trendlineSpanDays(trendline);
  if (adjective === 'Monthly') {
    const months = Math.max(1, Math.ceil(spanDays / 30));
    return `${adjective} ${noun} (last ${months} months)`;
  }
  if (adjective === 'Weekly') {
    const weeks = Math.max(1, Math.ceil(spanDays / 7));
    return `${adjective} ${noun} (last ${weeks} weeks)`;
  }
  return `${adjective} ${noun} (last ${Math.max(1, spanDays)} days)`;
}
